import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useCanEdit } from "@/api/session";
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  PageHeader,
  RelativeTime,
  Select,
  Switch,
  TextField,
  Toolbar,
  type DataTableColumn,
} from "@/components";
import { useDebouncedValue, useSearchParamsInPlace } from "@/lib/hooks";
import { useFormulas, useFormulaTags, type FormulaKind, type FormulaListItem } from "../api";
import { NewFormulaDialog } from "../components/NewFormulaDialog";
import { PasteDialog } from "../components/PasteDialog";
import { KIND_LABELS, VersionStatusBadge } from "../display";
import styles from "./FormulasPage.module.css";

const PAGE_SIZE = 50;
const KIND_OPTIONS = [
  { value: "all", label: "All kinds" },
  { value: "formula", label: "Formulas" },
  { value: "accord", label: "Accords" },
  { value: "dilution", label: "Dilutions" },
];

function isKind(value: string | null): value is FormulaKind {
  return value === "formula" || value === "accord" || value === "dilution";
}

/** Formulas, accords and dilutions with their latest version; search, kind, tag and archive filters in the URL. */
export function FormulasPage() {
  const [searchParams, setSearchParams] = useSearchParamsInPlace();
  const navigate = useNavigate();
  const canEdit = useCanEdit();
  const [newOpen, setNewOpen] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const q = searchParams.get("q") ?? "";
  const query = useDebouncedValue(q.trim(), 250);
  const kindParam = searchParams.get("kind");
  const kind = isKind(kindParam) ? kindParam : undefined;
  const tag = searchParams.get("tag") || undefined;
  const archived = searchParams.get("archived") === "1";
  const page = Math.max(0, Number(searchParams.get("page") ?? 0) || 0);
  const formulas = useFormulas({
    q: query || undefined,
    kind,
    tag,
    archived,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });
  const tags = useFormulaTags();

  function setParam(name: string, value: string | null) {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (!value || value === "all") next.delete(name);
        else next.set(name, value);
        if (name !== "page") next.delete("page");
        return next;
      },
      { preventScrollReset: name !== "page" },
    );
  }

  const columns: DataTableColumn<FormulaListItem>[] = [
    { id: "code", header: "Code", mono: true, width: "fit", cell: (formula) => formula.code },
    {
      id: "name",
      header: "Name",
      width: "wide",
      cell: (formula) => (
        <span className={styles.nameCell}>
          <Link to={`/formulas/${formula.id}`} className={styles.name}>
            {formula.name}
          </Link>
          {formula.tags.length || formula.family ? (
            <span className={styles.tags}>
              {formula.family ? <span className={styles.family}>{formula.family}</span> : null}
              {formula.tags.map((item) => (
                <span key={item} className={styles.tag}>
                  {item}
                </span>
              ))}
            </span>
          ) : null}
        </span>
      ),
    },
    { id: "kind", header: "Kind", width: "fit", cell: (formula) => KIND_LABELS[formula.kind] },
    {
      id: "latest",
      header: "Latest version",
      width: "fit",
      cell: (formula) =>
        formula.latest_version ? (
          <span className={styles.version}>
            <span className={styles.versionLabel}>{formula.latest_version.label}</span>
            <VersionStatusBadge status={formula.latest_version.status} />
          </span>
        ) : (
          "—"
        ),
    },
    {
      id: "released",
      header: "Last released",
      width: "fit",
      cell: (formula) => formula.latest_released_label ?? <span className={styles.subtle}>Not yet</span>,
    },
    {
      id: "lines",
      header: "Lines",
      numeric: true,
      width: "fit",
      cell: (formula) => formula.latest_version?.line_count ?? "—",
    },
    {
      id: "updated",
      header: "Updated",
      width: "fit",
      cell: (formula) => <RelativeTime value={formula.updated_at} />,
    },
  ];

  const total = formulas.data?.count ?? 0;
  const filtered = Boolean(query || kind || tag || archived);
  const tagOptions = [
    { value: "all", label: "All tags" },
    ...(tags.data ?? []).map((value) => ({ value, label: value })),
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        title="Formulas"
        description="Formulas, accords and dilutions. Released versions stay as they were; edits make a new version."
        actions={
          canEdit ? (
            <>
              <Button icon="clipboardCheck" onClick={() => setPasteOpen(true)}>
                Paste a formula…
              </Button>
              <Button variant="primary" icon="plus" onClick={() => setNewOpen(true)}>
                New formula
              </Button>
            </>
          ) : null
        }
      />
      <Toolbar label="Filter formulas">
        <TextField
          label="Search formulas"
          hideLabel
          type="search"
          placeholder="Search name, code, tag or project"
          value={q}
          onValueChange={(value) => setParam("q", value || null)}
          className={styles.search}
          size="sm"
        />
        <Select
          label="Kind"
          hideLabel
          size="sm"
          options={KIND_OPTIONS}
          value={kind ?? "all"}
          onValueChange={(value) => setParam("kind", value)}
        />
        <Select
          label="Tag"
          hideLabel
          size="sm"
          options={tagOptions}
          value={tag ?? "all"}
          onValueChange={(value) => setParam("tag", value)}
        />
        <Switch
          label="Archived"
          checked={archived}
          onCheckedChange={(checked) => setParam("archived", checked ? "1" : null)}
        />
      </Toolbar>
      <DataTable
        caption="Formulas"
        columns={columns}
        rows={formulas.data?.items}
        getRowId={(formula) => formula.id}
        loading={formulas.isPending}
        error={formulas.error}
        onRetry={() => void formulas.refetch()}
        rowActions={(formula) => [
          { label: "Open", icon: "flask", onSelect: () => void navigate(`/formulas/${formula.id}`) },
          {
            label: "Compare versions",
            icon: "list",
            disabled: formula.version_count < 2,
            onSelect: () => void navigate(`/formulas/${formula.id}/compare`),
          },
        ]}
        rowActionsLabel={(formula) => `Actions for ${formula.code} ${formula.name}`}
        empty={
          filtered ? (
            <EmptyState
              variant="inline"
              headingLevel={3}
              title="No formulas match"
              description="Try another search or clear the filters."
              actions={<Button onClick={() => setSearchParams({}, { replace: true })}>Clear filters</Button>}
            />
          ) : (
            <EmptyState
              variant="inline"
              headingLevel={3}
              icon="flask"
              title="No formulas yet"
              description="Write one line by line in %, parts or grams, or paste a formula from a post or a spreadsheet."
            />
          )
        }
      />
      {total > PAGE_SIZE ? (
        <nav className={styles.pager} aria-label="Pages">
          <Button size="sm" disabled={page === 0} onClick={() => setParam("page", String(page - 1))}>
            Previous
          </Button>
          <span className={styles.subtle}>
            {page * PAGE_SIZE + 1}–{Math.min(total, (page + 1) * PAGE_SIZE)} of {total}
          </span>
          <Button
            size="sm"
            disabled={(page + 1) * PAGE_SIZE >= total}
            onClick={() => setParam("page", String(page + 1))}
          >
            Next
          </Button>
        </nav>
      ) : null}
      {archived ? <Badge tone="neutral">Showing archived formulas</Badge> : null}
      <NewFormulaDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        onCreated={(formula) => void navigate(`/formulas/${formula.id}`)}
      />
      <PasteDialog
        open={pasteOpen}
        onOpenChange={setPasteOpen}
        onImported={(result) => void navigate(`/formulas/${result.formula_id}?version=${result.version_id}`)}
      />
    </div>
  );
}
